import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';
import type {
  BatchDeleteContactOpportunityDto,
  BatchUpdateContactOpportunityStatusDto,
  ContactOpportunityItem,
  ContactOpportunityPageResult,
  ContactOpportunityQueryParams,
  CreateContactOpportunityDto,
  UpdateContactOpportunityDto,
} from './types/contact-opportunity';

const request = createAppRequest();
const BASE_PATH = '/bff/zy-crm/contact-opportunities';

const unwrapContactOpportunityDetail = (resp: unknown): ContactOpportunityItem => {
  if (resp && typeof resp === 'object') {
    const obj = resp as Record<string, unknown>;
    const candidates = [
      obj,
      obj.data,
      obj.result,
      obj.payload,
      (obj.response as Record<string, unknown> | undefined)?.data,
    ];
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') {
        continue;
      }
      const record = candidate as Record<string, unknown>;
      if (
        typeof record.uuid === 'string' &&
        typeof record.contactUuid === 'string' &&
        typeof record.opportunityUuid === 'string'
      ) {
        return candidate as ContactOpportunityItem;
      }
    }
  }
  throw new Error('联系人商机详情返回结构异常');
};

export async function queryContactOpportunities(
  params: ContactOpportunityQueryParams,
  options?: ApiRequestOptions,
) {
  return request<ContactOpportunityPageResult>(BASE_PATH, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function queryContactOpportunityDetail(uuid: string, options?: ApiRequestOptions) {
  const resp = await request<unknown>(`${BASE_PATH}/${uuid}`, {
    method: 'GET',
    ...(options || {}),
  });
  return unwrapContactOpportunityDetail(resp);
}

export async function createContactOpportunity(
  payload: CreateContactOpportunityDto,
  options?: ApiRequestOptions,
) {
  return request<ContactOpportunityItem>(BASE_PATH, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function updateContactOpportunity(
  uuid: string,
  payload: UpdateContactOpportunityDto,
  options?: ApiRequestOptions,
) {
  return request<ContactOpportunityItem>(`${BASE_PATH}/${uuid}`, {
    method: 'PATCH',
    data: payload,
    ...(options || {}),
  });
}

export async function deleteContactOpportunity(uuid: string, options?: ApiRequestOptions) {
  return request<void>(`${BASE_PATH}/${uuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function batchDeleteContactOpportunities(
  payload: BatchDeleteContactOpportunityDto,
  options?: ApiRequestOptions,
) {
  return request<void>(`${BASE_PATH}/batch-delete`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function batchUpdateContactOpportunityStatus(
  payload: BatchUpdateContactOpportunityStatusDto,
  options?: ApiRequestOptions,
) {
  return request<void>(`${BASE_PATH}/batch-status`, {
    method: 'PATCH',
    data: payload,
    ...(options || {}),
  });
}

export async function restoreContactOpportunity(uuid: string, options?: ApiRequestOptions) {
  return request<void>(`${BASE_PATH}/${uuid}/restore`, {
    method: 'PATCH',
    ...(options || {}),
  });
}
